import {setupIframe} from '@dynamic-labs/iframe-setup';
import {
  UnmanagedMessenger,
  targetContextFromWindow,
  noopLogger,
  noopMetrics,
} from '@sail/comms';

import {Messenger} from './Messenger';
import type {
  WalletConnectMessageRegistry,
  Wallet,
  EvmTransactionData,
  EvmNativeTransactionData,
  SolanaTransactionData,
  EvmTypedData,
  EvmReadContractData,
  EvmAddress,
  DynamicIframeParams,
  DynamicIframeReadyData,
  ExchangePaymentData,
  ExchangePaymentParams,
} from './types';
import {getFrameSrc} from './utils/url';

export class DynamicWalletConnectWrapper {
  private params: DynamicIframeParams;

  private frame: HTMLIFrameElement;

  private frameSrc: URL;

  private parentElement!: HTMLElement;

  private messenger: Messenger<WalletConnectMessageRegistry> | undefined;

  private dynamicMessenger: UnmanagedMessenger | undefined;

  /* Dynamic state update callbacks for payins frontend to receive updates */

  private onActiveWalletUpdate:
    | ((activeWallet: Wallet | null) => void)
    | undefined;

  private onConnectedWalletsUpdate:
    | ((connectedWallets: Wallet[]) => void)
    | undefined;

  private onIframeReady: ((data: DynamicIframeReadyData) => void) | undefined;

  private onWalletConnectQrCodeReady: ((uri: string) => void) | undefined;

  FRAME_ID = 'crypto-payins-dynamic-wallet-connect-iframe';

  constructor(params: DynamicIframeParams) {
    this.params = params;
    this.frameSrc = getFrameSrc(params);
    this.frameSrc.searchParams.set('initial-parent-url', window.location.href);
    this.frame = this.createFrame();
  }

  private listenMessagesFromInnerIframe() {
    this.messenger!.on('DYNAMIC_HIDE_MODAL', () => {
      this.parentElement.style.height = '0';
      this.parentElement.style.width = '0';
    });
    this.messenger!.on('DYNAMIC_SET_ACTIVE_WALLET', (wallet) => {
      this.onActiveWalletUpdate?.(wallet);
    });
    this.messenger!.on('DYNAMIC_SET_CONNECTED_WALLETS', (wallets) => {
      this.onConnectedWalletsUpdate?.(wallets);
    });
    this.messenger!.on(
      'DYNAMIC_IFRAME_READY',
      (data: DynamicIframeReadyData) => {
        this.onIframeReady?.(data);
      },
    );
    this.messenger!.on('DYNAMIC_WALLET_CONNECT_QR_CODE_READY', (uri) => {
      this.onWalletConnectQrCodeReady?.(uri);
    });
  }

  createFrame() {
    const frame = document.createElement('iframe');

    frame.setAttribute('frameborder', '0');
    frame.setAttribute('tabindex', '0');
    frame.setAttribute('allowTransparency', 'true');
    frame.setAttribute('scrolling', 'no');
    frame.setAttribute('width', '100%');
    frame.setAttribute('height', '100%');

    frame.setAttribute('title', 'payins frame');
    frame.setAttribute('name', 'payins');
    frame.setAttribute('id', this.FRAME_ID);

    // Temp removing the sandbox attribute because CoinbaseWallet rejects requests from iframe context with the sandbox attribute set
    // frame.setAttribute(
    //   'sandbox',
    //   'allow-popups allow-scripts allow-same-origin allow-top-navigation',
    // );

    // Don't set src here - we'll set it in mount() after Messenger is ready
    frame.style.border = 'none';
    frame.style.position = 'absolute';
    frame.style.zIndex = '1';
    return frame;
  }

  /* Dynamic functions called by payins frontend to send messages to inner iframe */

  connectWallet(walletKey: string): Promise<Wallet> {
    return this.messenger!.request('DYNAMIC_CONNECT_WALLET', walletKey);
  }

  disconnectWallet(walletId: string): Promise<void> {
    return this.messenger!.request('DYNAMIC_DISCONNECT_WALLET', walletId);
  }

  switchWallet(walletId: string): Promise<void> {
    return this.messenger!.request('DYNAMIC_SWITCH_WALLET', walletId);
  }

  showModal(): void {
    console.log('[Dynamic-iframe] showModalDynamic() outer');
    this.parentElement.style.height = '100%';
    this.parentElement.style.width = '100%';
    this.messenger!.send('DYNAMIC_SHOW_MODAL', undefined);
  }

  signMessage(payload: string): Promise<string | undefined> {
    console.log('[Dynamic-iframe] signMessageDynamic() outer');
    return this.messenger!.request('DYNAMIC_SIGN_MESSAGE', payload);
  }

  evmSendTransaction(
    evmTransactionData: EvmTransactionData,
  ): Promise<`0x${string}`> {
    console.log('[Dynamic-iframe] evmSendTransactionDynamic() outer');
    return this.messenger!.request(
      'DYNAMIC_EVM_SEND_TRANSACTION',
      evmTransactionData,
    );
  }

  evmSendNativeTransaction(
    evmNativeTransactionData: EvmNativeTransactionData,
  ): Promise<`0x${string}`> {
    console.log('[Dynamic-iframe] evmSendNativeTransactionDynamic() outer');
    return this.messenger!.request(
      'DYNAMIC_EVM_SEND_NATIVE_TRANSACTION',
      evmNativeTransactionData,
    );
  }

  solanaSignTransaction(
    solanaTransactionData: SolanaTransactionData,
  ): Promise<string> {
    console.log('[Dynamic-iframe] solanaSignTransactionDynamic() outer');
    return this.messenger!.request(
      'DYNAMIC_SOLANA_SIGN_TRANSACTION',
      solanaTransactionData,
    );
  }

  evmSignTypedData(
    evmTypedData: EvmTypedData,
  ): Promise<EvmAddress | undefined> {
    console.log('[Dynamic-iframe] evmSignTypedDataDynamic() outer');
    return this.messenger!.request('DYNAMIC_EVM_SIGN_TYPED_DATA', evmTypedData);
  }

  evmReadContract<T>(evmReadContractData: EvmReadContractData): Promise<T> {
    console.log('[Dynamic-iframe] evmReadContractDynamic() outer');
    return this.messenger!.request(
      'DYNAMIC_EVM_READ_CONTRACT',
      evmReadContractData,
    ) as Promise<T>;
  }

  switchNetwork(chainId: number | string) {
    console.log('[Dynamic-iframe] switchNetworkDynamic() outer', chainId);
    return this.messenger!.request('DYNAMIC_SWITCH_NETWORK', chainId);
  }

  createExchangePayment({
    exchange,
    address,
    token,
    chainName,
    network,
    currency,
    tokenAmount,
    merchantName,
    description,
  }: ExchangePaymentParams): Promise<ExchangePaymentData> {
    console.log(
      '[Dynamic-iframe] createExchangePayment() outer',
      exchange,
      address,
      token,
      chainName,
      network,
      currency,
      tokenAmount,
      merchantName,
      description,
    );
    return this.messenger!.request('DYNAMIC_CREATE_EXCHANGE_PAYMENT', {
      exchange,
      address,
      token,
      chainName,
      network,
      currency,
      tokenAmount,
      merchantName,
      description,
    });
  }

  /* */

  /* Dynamic state update callback setter functions (payins frontend calls these to set the callback to receive updates) */

  setOnActiveWallet(callback: (activeWallet: Wallet | null) => void) {
    this.onActiveWalletUpdate = callback;
  }

  setOnConnectedWallets(callback: (connectedWallets: Wallet[]) => void) {
    this.onConnectedWalletsUpdate = callback;
  }

  setOnIframeReady(callback: (data: DynamicIframeReadyData) => void) {
    this.onIframeReady = callback;
  }

  setOnWalletConnectQrCodeReady(callback: ((uri: string) => void) | undefined) {
    this.onWalletConnectQrCodeReady = callback;
  }

  syncParentUrl(url = window.location.href): void {
    this.dynamicMessenger?.sendMessage({
      eventName: 'UPDATE_PARENT_URL',
      args: [{url}],
    });
  }

  mount(elementId: string): string {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error('Element not found');
    }
    if (this.parentElement !== undefined) {
      throw new Error('WalletConnect already mounted');
    }

    this.parentElement = element;

    this.parentElement.style.height = '0';
    this.parentElement.style.width = '0';
    this.parentElement.style.position = 'absolute';
    this.parentElement.appendChild(this.frame);

    if (!this.frame.contentWindow) {
      throw new Error('Frame content window not found');
    }
    this.messenger = new Messenger<WalletConnectMessageRegistry>(
      this.frame.contentWindow,
      this.frameSrc.origin,
    );
    this.dynamicMessenger = new UnmanagedMessenger({
      logger: noopLogger,
      metrics: noopMetrics,
      remote: {
        context: targetContextFromWindow(this.frame.contentWindow),
        origin: this.frameSrc.origin,
      },
      self: {
        context: targetContextFromWindow(window),
      },
    });
    this.listenMessagesFromInnerIframe();

    // Set the src after Messenger is set up to avoid missing early messages
    this.frame.src = this.frameSrc.toString();
    setupIframe(this.frame, this.frameSrc.origin);

    return this.FRAME_ID;
  }
}
